import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useRef, useState } from 'react';
import { ExpandedTooltip, Icon, BackdropLoader } from '@livable/saas-react-components';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import DocViewer from '@cyntler/react-doc-viewer';
import { getFileTypeFromName, getRenderersForFileName } from './fileRenderers';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
import useStyles from './styles';
import { useStore } from 'src/stores';
import { FilePreviewModalDraw } from './draw';
import NoDataPlaceholder from 'src/components/shared/NoDataPlaceholder';
export const FilePreviewPlain = observer(_ref => {
  let fileName = _ref.fileName,
    fileSize = _ref.fileSize,
    docUri = _ref.docUri,
    fileBlobs = _ref.fileBlobs,
    isDisabledParent = _ref.isDisabledParent,
    isPreviewForm = _ref.isPreviewForm,
    handleDelete = _ref.handleDelete,
    fileId = _ref.fileId,
    uploadEditedFile = _ref.uploadEditedFile,
    _ref$isPreviewOnly = _ref.isPreviewOnly,
    isPreviewOnly = _ref$isPreviewOnly === void 0 ? false : _ref$isPreviewOnly;
  const store = useStore();
  const classes = useStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isFileEdit = _useState2[0],
    setIsFileEdit = _useState2[1];
  const canvasOverflowWrapperRef = useRef(null);
  const canvasWrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const colorRef = useRef(null);
  const sizeRef = useRef(null);
  const canvasWidth = 1280;
  const canvasHeight = 647;
  let drawRestore = [];
  let drawRestoreIndex = 0;
  const _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    filesFromBlobs = _useState4[0],
    setFilesFromBlobs = _useState4[1];
  if (!!fileBlobs) {
    setFilesFromBlobs(fileBlobs.map(file => {
      return {
        uri: window.URL.createObjectURL(file),
        fileName: file.name
      };
    }));
  }
  const elt = (name, attributes) => {
    let node = document.createElement(name);
    if (attributes) {
      for (let attr in attributes) if (attributes.hasOwnProperty(attr)) node.setAttribute(attr, attributes[attr]);
    }
    return node;
  };
  const relativePos = (event, element) => {
    let rect = element.getBoundingClientRect();
    return {
      x: Math.floor(event.clientX - rect.left) * (10 / (10 - (store.files.drawIndex - 1))),
      y: Math.floor(event.clientY - rect.top) * (10 / (10 - (store.files.drawIndex - 1)))
    };
  };
  const trackDrag = (onMove, onEnd) => {
    const end = event => {
      removeEventListener('mousemove', onMove);
      removeEventListener('mouseup', end);
      if (onEnd) onEnd(event);
    };
    addEventListener('mousemove', onMove);
    addEventListener('mouseup', end);
  };
  let tools = Object.create(null);
  tools.line = function (event, cx, onEnd) {
    cx.lineCap = 'round';
    cx.fillStyle = colorRef.current.value;
    cx.strokeStyle = colorRef.current.value;
    let pos = relativePos(event, cx.canvas);
    trackDrag(cEvent => {
      cx.beginPath();
      cx.moveTo(pos.x, pos.y);
      pos = relativePos(cEvent, cx.canvas);
      cx.lineTo(pos.x, pos.y);
      cx.stroke();
    }, onEnd);
  };
  tools.erase = function (event, cx, onEnd) {
    cx.lineCap = 'round';
    cx.fillStyle = '#FFF';
    cx.strokeStyle = '#FFF';
    let pos = relativePos(event, cx.canvas);
    trackDrag(cEvent => {
      cx.beginPath();
      cx.moveTo(pos.x, pos.y);
      pos = relativePos(cEvent, cx.canvas);
      cx.lineTo(pos.x, pos.y);
      cx.stroke();
    }, onEnd);
  };
  tools.rectangle = function (event, cx) {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let leftX,
      rightX,
      topY,
      bottomY,
      clientX = event.clientX,
      clientY = event.clientY;
    let placeholder = elt('div', {
      class: 'drawPlaceholder'
    });
    let initialPos = relativePos(event, cx.canvas);
    let xOffset = clientX - initialPos.x,
      yOffset = clientY - initialPos.y;
    trackDrag(cEvent => {
      document.body.appendChild(placeholder);
      let currentPos = relativePos(cEvent, cx.canvas),
        startX = initialPos.x,
        startY = initialPos.y;
      if (startX < currentPos.x) {
        leftX = startX;
        rightX = currentPos.x;
      } else {
        leftX = currentPos.x;
        rightX = startX;
      }
      if (startY < currentPos.y) {
        topY = startY;
        bottomY = currentPos.y;
      } else {
        topY = currentPos.y;
        bottomY = startY;
      }
      placeholder.style.background = cx.fillStyle;
      placeholder.style.left = leftX + xOffset + 'px';
      placeholder.style.top = topY + yOffset + 'px';
      placeholder.style.width = (rightX - leftX) / (10 / (10 - (store.files.drawIndex - 1))) + 'px';
      placeholder.style.height = (bottomY - topY) / (10 / (10 - (store.files.drawIndex - 1))) + 'px';
    }, () => {
      cx.fillRect(leftX, topY, rightX - leftX, bottomY - topY);
      document.body.removeChild(placeholder);
      drawRestore.push(ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height));
      drawRestoreIndex += 1;
    });
  };
  const startDrawing = event => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (drawRestore.length > 0) drawRestore.splice(drawRestoreIndex + 1, drawRestore.length - 1);
    if (drawRestoreIndex === 0 && drawRestore.length === 0) drawRestore.push(ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height));
    if (store.files.drawType === 'line') {
      tools.line(event, ctx);
    } else if (store.files.drawType === 'erase') {
      tools.erase(event, ctx);
    } else if (store.files.drawType === 'rectangle') {
      tools.rectangle(event, ctx);
    }
  };
  const endDrawing = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (store.files.drawType === 'line' || store.files.drawType === 'erase') {
      drawRestore.push(ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height));
      drawRestoreIndex += 1;
    }
  };
  const handleColor = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = colorRef.current.value;
    ctx.strokeStyle = colorRef.current.value;
  };
  const handleSize = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = sizeRef.current.value;
  };
  const blockTypeStyle = imgHeight => {
    if (imgHeight < canvasHeight) {
      canvasWrapperRef.current.style.display = "flex";
    } else {
      canvasWrapperRef.current.style.display = "block";
    }
  };
  const zoomOut = () => {
    if (store.files.drawIndex < 10) {
      canvasRef.current.style.transform = "scale(".concat(1 - store.files.drawIndex * 0.1, ")");
      const cw = canvasRef.current.getBoundingClientRect().width;
      const ch = canvasRef.current.getBoundingClientRect().height;
      blockTypeStyle(ch);
      canvasOverflowWrapperRef.current.style.width = "".concat(cw, "px");
      canvasOverflowWrapperRef.current.style.height = "".concat(ch, "px");
      store.files.drawIndexIncrement();
    }
  };
  const autoSize = () => {
    for (let count = 0; count < 9; count++) {
      if (!(canvasRef.current.getBoundingClientRect().width > canvasWidth || canvasRef.current.getBoundingClientRect().height > canvasHeight)) {
        break;
      }
      zoomOut();
    }
  };
  const startEdit = async () => {
    setIsFileEdit(true);
    let pdfFile = null;
    if (getFileTypeFromName(fileName) === 'pdf') {
      if (store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled) {
        const pdfFilePresignedUrlRes = await store.files.convertToImageByIdAsync.run({
          fileId: fileId,
          useBlogResp: store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled
        });
        if (pdfFilePresignedUrlRes !== null && pdfFilePresignedUrlRes !== void 0 && pdfFilePresignedUrlRes.isSuccessful) {
          const res = await fetch(pdfFilePresignedUrlRes.response);
          pdfFile = await res.blob();
        }
      } else {
        pdfFile = await store.files.convertToImageByIdAsync.run({
          fileId: fileId
        });
      }
    }
    setTimeout(() => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      let baseImage = new Image();
      baseImage.crossOrigin = 'anonymous';
      baseImage.src = pdfFile ? URL.createObjectURL(pdfFile) : docUri;
      baseImage.onload = () => {
        ctx.canvas.width = baseImage.width;
        ctx.canvas.height = baseImage.height;
        canvasOverflowWrapperRef.current.style.width = "".concat(baseImage.width, "px");
        canvasOverflowWrapperRef.current.style.height = "".concat(baseImage.height, "px");
        ctx.drawImage(baseImage, 0, 0);
        autoSize();
      };
    }, 0);
  };
  const zoomIn = () => {
    if (store.files.drawIndex > 1) {
      store.files.drawIndexDecrement();
      canvasRef.current.style.transform = "scale(".concat(1 - store.files.drawIndex * 0.1 + 0.1, ")");
      const cw = canvasRef.current.getBoundingClientRect().width;
      const ch = canvasRef.current.getBoundingClientRect().height;
      blockTypeStyle(ch);
      canvasOverflowWrapperRef.current.style.width = "".concat(cw, "px");
      canvasOverflowWrapperRef.current.style.height = "".concat(ch, "px");
    }
  };
  const handleDeleteFile = async () => {
    if (handleDelete) handleDelete();
    store.files.drawIndexReset();
    store.files.viewIndexReset();
    store.files.resetDrawForm();
  };
  const handleDownloadFile = async () => {
    if (!fileId) return;
    if (store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled) {
      const resp = await store.files.getPresignedFileDownloadUrlAsync.run({
        fileId,
        isPreview: false
      });
      if (resp !== null && resp !== void 0 && resp.isSuccessful && resp.response) {
        window.open(resp.response, '_blank');
      }
      return;
    }
    window.open(store.files.getFilePreviewLink({
      fileId
    }), '_blank');
  };
  const saveDrawAsImage = async () => {
    canvasRef.current.toBlob(async blob => {
      var _uploadedEditedFileRe;
      const editedFile = new File([blob], "".concat(fileName === null || fileName === void 0 ? void 0 : fileName.split('.').shift(), ".jpeg"));
      let uploadedEditedFileResult;
      if (store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled) {
        uploadedEditedFileResult = await store.files.uploadFileViaPresignedUrlExperimentAsync.run({
          file: editedFile,
          createdBy: store.account.userProfile.id
        });
      } else {
        uploadedEditedFileResult = await store.files.uploadFileAsync.run({
          file: editedFile
        });
      }

      // const uploadedEditedFileResult = await store.files.uploadFileAsync.run({
      //   file: editedFile,
      // });

      if ((_uploadedEditedFileRe = uploadedEditedFileResult) !== null && _uploadedEditedFileRe !== void 0 && _uploadedEditedFileRe.isSuccessful) {
        await uploadEditedFile(uploadedEditedFileResult.response);
        store.files.drawIndexReset();
        setIsFileEdit(false);
      }
    }, 'image/jpeg', 0.6);
  };
  const undo = () => {
    if (drawRestoreIndex < 1) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.putImageData(drawRestore[drawRestoreIndex - 1], 0, 0);
    drawRestoreIndex--;
  };
  const redo = () => {
    if (drawRestoreIndex > drawRestore.length - 2) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.putImageData(drawRestore[drawRestoreIndex + 1], 0, 0);
    drawRestoreIndex++;
  };
  const imageZooming = action => {
    const img = document.getElementById('image-img');
    const wr = document.getElementById('image-renderer');
    const imgWidth = img.clientWidth;
    const imgHeight = img.clientHeight;
    if (action === 'INCREMENT') store.files.viewIndexIncrement();
    if (action === 'DECREMENT') store.files.viewIndexDecrement();
    if (imgWidth > imgHeight) {
      img.style.width = "".concat(store.files.viewIndex, "%");
    } else {
      img.style.height = "".concat(store.files.viewIndex, "%");
    }
    if (store.files.viewIndex > 100) {
      wr.style.display = "block";
    } else {
      wr.style.display = "flex";
    }
    img.style.maxWidth = "".concat(store.files.viewIndex, "%");
    img.style.maxHeight = "".concat(store.files.viewIndex, "%");
  };
  const imageAutoWidth = () => {
    const img = document.getElementById('image-img');
    const wr = document.getElementById('image-renderer');
    if (img.clientWidth + 100 > wr.clientWidth) {
      while (img.clientWidth > wr.clientWidth) {
        imageZooming('DECREMENT');
      }
    } else {
      while (wr.clientWidth > img.clientWidth + 100) {
        imageZooming('INCREMENT');
      }
    }
  };

  // useEffect(() => {
  //   // if (!isFileEdit && ['jpg', 'jpeg', 'png'].includes(getFileTypeFromName(fileName))) {
  //   //   setTimeout(() => {
  //   //     imageAutoWidth();
  //   //   }, 5000);
  //   // }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isFileEdit]);

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.modalContentPlain, " ").concat(classes.modalContentWithForm, " ").concat(isFileEdit ? 'isFileEdit' : '')
  }, /*#__PURE__*/React.createElement("div", {
    className: isPreviewOnly ? classes.modalFilePreviewOnly : classes.modalFile
  }, isPreviewForm && /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.fileController, " plain")
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.fileControllerEdit
  }, !isFileEdit && ['jpg', 'jpeg', 'png'].includes(getFileTypeFromName(fileName)) && /*#__PURE__*/React.createElement("div", {
    className: classes.drawControllers
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.drawController
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Zoom In'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    onClick: () => imageZooming('INCREMENT'),
    'custom-attribute': '322-FilePreviewModal'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "zoom-in"
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.drawController
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Zoom Out'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    onClick: () => imageZooming('DECREMENT'),
    'custom-attribute': '323-FilePreviewModal'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "zoom-out"
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.drawController
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Full Width'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    onClick: () => imageAutoWidth(),
    'custom-attribute': '326-FilePreviewModal'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "arrows"
  }))))), isFileEdit && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.drawControllers
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.drawController
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Zoom In'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    onClick: () => zoomIn(),
    'custom-attribute': '324-FilePreviewModal'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "zoom-in"
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.drawController
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Zoom Out'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    onClick: () => zoomOut(),
    'custom-attribute': '325-FilePreviewModal'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "zoom-out"
  }))))), /*#__PURE__*/React.createElement(FilePreviewModalDraw, null), /*#__PURE__*/React.createElement("div", {
    className: classes.colorController
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Change color'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("input", {
    type: "color",
    onChange: handleColor,
    ref: colorRef
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.sizeController
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Brush size'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("input", {
    type: "range",
    min: "1",
    max: "30",
    step: "1",
    defaultValue: "1",
    onChange: handleSize,
    ref: sizeRef
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.drawControllers
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.drawController
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Undo'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    onClick: () => undo(),
    'custom-attribute': '326-FilePreviewModal'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "prev"
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.drawController
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Redo'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    onClick: () => redo(),
    'custom-attribute': '327-FilePreviewModal'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "next"
  }))))))), /*#__PURE__*/React.createElement("div", {
    className: classes.fileControllerDelete
  }, !isFileEdit && !isPreviewOnly && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Edit file'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    className: classes.viewerIconButton,
    onClick: () => startEdit(),
    disabled: !['pdf', 'jpg', 'jpeg', 'png'].includes(getFileTypeFromName(fileName)) || isDisabledParent,
    'custom-attribute': '328-FilePreviewModal'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "edit"
  })))), /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Download file'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    className: classes.viewerIconButton,
    onClick: () => handleDownloadFile(),
    disabled: isDisabledParent,
    'custom-attribute': '3320-FilePreviewModal'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "file-download"
  })))), /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Delete file'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    className: classes.viewerIconButton,
    onClick: () => handleDeleteFile(),
    disabled: isDisabledParent,
    'custom-attribute': '329-FilePreviewModal'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "trash"
  }))))), isFileEdit && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    size: "small",
    onClick: () => {
      store.files.drawIndexReset();
      setIsFileEdit(false);
    },
    'custom-attribute': '330-FilePreviewModal'
  }, "Cancel changes"), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    onClick: () => saveDrawAsImage(),
    'custom-attribute': '331-FilePreviewModal'
  }, "Save image")))), !isPreviewForm && ['jpg', 'jpeg', 'png'].includes(getFileTypeFromName(fileName)) && /*#__PURE__*/React.createElement("div", {
    className: classes.fileController
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.fileControllerEdit
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.drawControllers
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.drawController
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Zoom In'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    onClick: () => imageZooming('INCREMENT'),
    'custom-attribute': '332-FilePreviewModal'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "zoom-in"
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.drawController
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('Zoom Out'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    onClick: () => imageZooming('DECREMENT'),
    'custom-attribute': '333-FilePreviewModal'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "zoom-out"
  }))))))), isPreviewForm && isFileEdit && /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.canvas, " plain"),
    ref: canvasWrapperRef
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.canvasWrapper,
    ref: canvasOverflowWrapperRef
  }, /*#__PURE__*/React.createElement("canvas", {
    id: "canvas",
    ref: canvasRef,
    width: canvasWidth,
    height: canvasHeight,
    onMouseDown: startDrawing,
    onMouseUp: endDrawing
  }))), !isFileEdit && /*#__PURE__*/React.createElement(React.Fragment, null, fileSize && fileSize > 3.5 && ['doc', 'docx'].includes(getFileTypeFromName(fileName)) ? /*#__PURE__*/React.createElement("div", {
    className: classes.microsoftFileSizePreviewError
  }, /*#__PURE__*/React.createElement(NoDataPlaceholder, {
    text: t('general.preview_file_type_warning')
  })) : !filesFromBlobs.length ? /*#__PURE__*/React.createElement(DocViewer, {
    key: docUri,
    className: classes.DocViewer,
    prefetchMethod: "GET",
    documents: [{
      uri: docUri,
      fileName: fileName,
      fileType: getFileTypeFromName(fileName)
    }],
    pluginRenderers: getRenderersForFileName(fileName),
    config: {
      header: {
        disableHeader: true
      },
      pdfVerticalScrollByDefault: true
    },
    requestHeaders: store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled ? undefined : {
      Authorization: "Bearer ".concat(localStorage.getItem(AUTH_TOKEN_KEYS.accessToken))
    },
    style: {
      height: 584
    }
  }) : /*#__PURE__*/React.createElement(DocViewer, {
    prefetchMethod: "GET",
    documents: filesFromBlobs,
    pluginRenderers: getRenderersForFileName(fileName),
    config: {
      header: {
        disableHeader: true
      },
      pdfVerticalScrollByDefault: true
    },
    style: {
      height: 584
    }
  })))), /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: store.files.convertToImageByIdAsync.inProgress || store.files.uploadFileAsync.inProgress || store.files.getFileInfoAsync.inProgress
  }));
});